/**
 * One inbound-relation section in the Related Data Panel.
 *
 * Collapsed by default. Expanding fires `useInboundRelationData` (COUNT +
 * first-N rows) and renders `RandomDotmSpinner` until the data lands.
 *
 * Slice 1: the header "Open" primary button falls back to "open in new TE
 * tab" (no breadcrumb tree yet — Slice 3 rewires).
 */

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Maximize2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { RelatedDataMiniGrid } from './RelatedDataMiniGrid';
import { useInboundRelationData } from '../hooks/useInboundRelationData';
import { openTableExplorerTab } from '../../table-explorer/utils/openTableExplorerTab';
import { buildInboundForeignKeyFilters } from '../../table-explorer/utils/inboundForeignKeyUtils';
import { DrillOpenInMenu } from '../../common/components/DrillOpenInMenu';
import type { InboundRelation } from '../types/singleRowTypes';
import type { DatabaseEngineId } from '../../../../core/engines';

type RelatedDataSectionProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly engine: DatabaseEngineId;
  readonly relation: InboundRelation;
  readonly parentRow: Record<string, unknown>;
  readonly expanded: boolean;
  readonly onToggleExpanded: () => void;
  /**
   * Slice 3 (EP5) — when set, the "Open" button drills into the active TE
   * tab's navigation tree (push a filtered table-node) instead of opening
   * a new tab. Falls back to the old behavior when undefined (no chip-row
   * support, e.g. EC1).
   */
  readonly onSectionOpen?: () => void;
  /**
   * Slice 3 (EP6) — drill-icon click / mini-grid right-click handler.
   * `via` discriminates icon vs context-menu so the parent can emit the
   * matching `source` value (`related-row-icon` vs `related-row-context`).
   */
  readonly onRowOpen?: (args: {
    row: Record<string, unknown>;
    via: 'icon' | 'context-menu';
    modifierKey: boolean;
  }) => void;
};

export function RelatedDataSection({
  connectionId,
  database,
  engine,
  relation,
  parentRow,
  expanded,
  onToggleExpanded,
  onSectionOpen,
  onRowOpen,
}: RelatedDataSectionProps) {
  const { track } = useAnalytics();
  const [hasTrackedExpand, setHasTrackedExpand] = useState(false);

  const { data, isLoading, error } = useInboundRelationData({
    connectionId,
    database,
    relation,
    parentRow,
    enabled: expanded,
    limit: 3,
  });

  // Emit analytics exactly once per expand (until collapsed-and-re-expanded
  // recreates the gating). Includes rowCount once the data lands.
  useEffect(() => {
    if (!expanded) {
      setHasTrackedExpand(false);
      return;
    }
    if (hasTrackedExpand) return;
    if (isLoading) return;
    if (error) return;
    const rowCount = data?.rows.length ?? 0;
    track('feature:single_row_related_expand', {
      engine,
      rowCount,
      viaLooseFk: relation.isLoose,
    });
    setHasTrackedExpand(true);
  }, [expanded, isLoading, error, data, hasTrackedExpand, track, engine, relation.isLoose]);

  const handleOpen = () => {
    // Slice 3 (EP5): when the parent provides the drill-in handler, use it.
    if (onSectionOpen) {
      onSectionOpen();
      return;
    }
    // Fallback: old "open in new TE tab" behavior.
    const build = buildInboundForeignKeyFilters({
      foreignKey: relation.source,
      parentRow,
      looseRel: undefined,
    });
    if (!build.filters) return;
    const title = `${relation.fromSchema ? `${relation.fromSchema}.` : ''}${relation.fromTable} [FK]`;
    openTableExplorerTab({
      connectionId,
      database,
      schema: relation.fromSchema,
      tableName: relation.fromTable,
      title,
      filters: build.filters,
    });
  };

  return (
    <section
      className="rounded-md border border-border bg-background/70"
      data-testid={`related-data-section-${relation.relationKey}`}
    >
      <div className="flex items-center justify-between gap-2 px-2 py-1.5">
        <button
          type="button"
          className="flex items-center gap-2 min-w-0 flex-1 text-left"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          data-testid={`related-section-toggle-${relation.relationKey}`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
          )}
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1">
              <span className="truncate">
                {relation.fromSchema ? `${relation.fromSchema}.` : ''}
                {relation.fromTable}
              </span>
              {relation.isLoose && (
                <span
                  className="inline-flex items-center px-1 text-[9px] rounded border border-teal-500/30 bg-teal-500/10 text-teal-500"
                  title="Loose relationship"
                >
                  loose
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground truncate">
              {relation.fromColumn} → {relation.toColumn}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleOpen}
            data-testid="related-section-open"
            title="Open in Table Explorer (drill in)"
          >
            <Maximize2 className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
            Open
          </Button>
          <DrillOpenInMenu
            connectionId={connectionId}
            database={database}
            engineId={engine}
            source="related-section-menu"
            buttonTestId="related-section-more-menu"
            target={{
              kind: 'table',
              schema: relation.fromSchema,
              table: relation.fromTable,
            }}
          />
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border">
          {isLoading && (
            <div
              className="flex items-center gap-2 px-3 py-3 text-xs text-muted-foreground"
              data-testid="random-dotm-spinner"
            >
              <RandomDotmSpinner size={14} />
              Loading {relation.fromTable}…
            </div>
          )}
          {error !== null && error !== undefined && !isLoading && (
            <div className="text-xs text-destructive px-3 py-2">
              Failed to load related rows.
            </div>
          )}
          {!isLoading && !error && data && (
            <RelatedDataMiniGrid
              relation={relation}
              rows={data.rows}
              columns={data.columns}
              connectionId={connectionId}
              database={database}
              engine={engine}
              parentRow={parentRow}
              onRowOpen={onRowOpen}
            />
          )}
        </div>
      )}
    </section>
  );
}
